import { useRef } from 'react';
import { useScrollAnimation, useCountUp } from '../../../hooks/useScrollAnimation';

const CERTS = [
  { icon: 'ri-shield-check-fill', label: 'NAPIT Registered' },
  { icon: 'ri-award-fill', label: 'Part P Certified' },
  { icon: 'ri-car-fill', label: 'OLEV EV Installer' },
  { icon: 'ri-file-certificate-fill', label: 'Fully Insured' },
];

function StatItem({
  target,
  suffix,
  label,
  isVisible,
  delay,
}: {
  target: number;
  suffix: string;
  label: string;
  isVisible: boolean;
  delay: number;
}) {
  const count = useCountUp(target, isVisible);

  return (
    <div
      className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <p className="font-playfair text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent tabular-nums">
        {count}{suffix}
      </p>
      <p className="text-white/50 text-xs mt-1">{label}</p>
    </div>
  );
}

export default function About() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();
  const statsRef = useRef<HTMLDivElement>(null);
  const { ref: statsAnimRef, isVisible: statsVisible } = useScrollAnimation(0.3);

  return (
    <section id="about" className="py-20 md:py-24 bg-[#0D1E35] relative overflow-hidden">
      {/* Gold diagonal accent */}
      <div
        className="absolute top-0 left-0 w-full h-1"
        style={{ background: 'linear-gradient(90deg, transparent, #C8A96A, transparent)' }}
      />
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
        <img
          src="https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/90758309-4e57-4704-ada6-1299ec143e1f_3eb45589-c339-4fd1-9e2d-32cca06dacf9-removebg-preview.png?v=2a77e748ecd680d6c594c6350bb285eb"
          alt=""
          className="w-full h-full object-contain"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Image side */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`flex-shrink-0 w-full lg:w-auto transition-all duration-1000 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="relative w-full max-w-sm mx-auto">
              {/* Gold frame corners */}
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t-2 border-l-2 border-[#C8A96A] z-10" />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-[#C8A96A] z-10" />
              {/* Gold glow behind */}
              <div
                className="absolute inset-0 rounded-2xl opacity-20 blur-2xl"
                style={{ background: 'radial-gradient(ellipse, #C8A96A, transparent 70%)' }}
              />
              <img
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/75890358aaa558161faa25ace3b04c48.png"
                alt="Lee - Owner & Electrician at Quantum Electrical Services Sleaford"
                className="w-full object-cover object-top rounded-2xl relative z-10"
                style={{ height: 460 }}
              />
              {/* Floating badge */}
              <div className="absolute -right-4 md:-right-6 bottom-10 bg-gradient-to-br from-[#C8A96A] to-[#A8894F] text-black rounded-2xl p-4 shadow-2xl z-20">
                <p className="font-bold text-2xl leading-none">10+</p>
                <p className="text-xs font-semibold mt-1 whitespace-nowrap">Years Experience</p>
              </div>
            </div>
          </div>

          {/* Text side */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`flex-1 w-full transition-all duration-1000 delay-200 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-3">About Us</p>

            {/* H2 with gold underline line reveal */}
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Sleaford&apos;s Trusted{' '}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">
                  Local Electrician
                </span>
                <span
                  className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] transition-all duration-1000 delay-700 ${rightVisible ? 'w-full' : 'w-0'}`}
                />
              </span>
            </h2>

            <p className="text-white/70 text-base leading-relaxed mb-4">
              Hi, I&apos;m Lee — owner and lead electrician at Quantum Electrical Services. 
              I&apos;m proud to serve Sleaford, Lincolnshire and surrounding areas as your 
              trusted, reliable, and fully qualified local electrician.
            </p>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              I specialise in domestic and commercial installations, maintenance and repairs, 
              and I believe in getting the job done right the first time. You can trust me to 
              turn up on time, communicate clearly, and leave your home cleaner than I found it.
            </p>

            {/* Certifications */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {CERTS.map(({ icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:border-[#C8A96A]/40 transition-colors duration-300"
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${icon} text-[#C8A96A] text-xl`} />
                  </div>
                  <span className="text-white/80 text-sm font-medium">{label}</span>
                </div>
              ))}
            </div>

            {/* Counting Stats */}
            <div
              ref={statsAnimRef as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-4 gap-4 border-t border-white/10 pt-8"
            >
              <StatItem target={10} suffix="+" label="Years Experience" isVisible={statsVisible} delay={0} />
              <StatItem target={500} suffix="+" label="Happy Clients" isVisible={statsVisible} delay={150} />
              <StatItem target={5} suffix="★" label="Google Rating" isVisible={statsVisible} delay={300} />
              <StatItem target={100} suffix="%" label="Satisfaction" isVisible={statsVisible} delay={450} />
            </div>
          </div>
        </div>
      </div>

      {/* Gold bottom line */}
      <div
        className="absolute bottom-0 left-0 w-full h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #C8A96A40, transparent)' }}
      />
    </section>
  );
}
